import { Link } from 'react-router-dom'
import type { FeaturedJob } from '../../types'

interface FeaturedJobsProps {
  jobs: FeaturedJob[]
}

function formatSalary(min?: number, max?: number, currency?: string): string {
  if (!min && !max) return ''
  const formatter = new Intl.NumberFormat('en-IE', {
    style: 'currency',
    currency: currency || 'EUR',
    maximumFractionDigits: 0,
  })
  if (min && max) {
    return `${formatter.format(min)} - ${formatter.format(max)}`
  }
  return min ? `From ${formatter.format(min)}` : `Up to ${formatter.format(max!)}`
}

function timeAgo(date: string): string {
  const now = new Date()
  const published = new Date(date)
  const diffMs = now.getTime() - published.getTime()
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

  if (diffDays === 0) return 'Today'
  if (diffDays === 1) return 'Yesterday'
  if (diffDays < 7) return `${diffDays} days ago`
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`
  return `${Math.floor(diffDays / 30)} months ago`
}

const jobTypeLabels: Record<string, string> = {
  full_time: 'Full-time',
  part_time: 'Part-time',
  contract: 'Contract',
  freelance: 'Freelance',
  internship: 'Internship',
}

const locationTypeLabels: Record<string, string> = {
  remote: 'Remote',
  onsite: 'On-site',
  hybrid: 'Hybrid',
}

function JobCard({ job }: { job: FeaturedJob }) {
  return (
    <Link
      to={`/jobs/${job.id}`}
      className="block bg-white rounded-lg border border-gray-200 p-6 hover:shadow-lg transition-shadow relative"
    >
      {/* Featured/Urgent badges */}
      <div className="absolute top-4 right-4 flex gap-2">
        {job.is_featured && (
          <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-800 rounded">
            Featured
          </span>
        )}
        {job.is_urgent && (
          <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-red-100 text-red-800 rounded">
            Urgent
          </span>
        )}
      </div>

      {/* Company info */}
      <div className="flex items-start gap-3 mb-4">
        {job.company_logo ? (
          <img
            src={job.company_logo}
            alt={job.company_name}
            className="w-12 h-12 rounded-lg object-cover border border-gray-200"
          />
        ) : (
          <div className="w-12 h-12 rounded-lg bg-gray-100 flex items-center justify-center text-gray-400">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
          </div>
        )}
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-gray-900 truncate pr-20">{job.title}</h3>
          <p className="text-sm text-gray-600">{job.company_name}</p>
        </div>
      </div>

      {/* Job details */}
      <div className="flex flex-wrap gap-2 mb-4 text-sm">
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full bg-primary-50 text-primary-700">
          {jobTypeLabels[job.job_type] || job.job_type}
        </span>
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full bg-gray-100 text-gray-700">
          {locationTypeLabels[job.location_type] || job.location_type}
          {job.location_city && ` - ${job.location_city}`}
        </span>
      </div>

      {/* Salary */}
      {job.show_salary && (job.salary_min || job.salary_max) && (
        <div className="text-sm font-medium text-green-600 mb-3">
          {formatSalary(job.salary_min, job.salary_max, job.salary_currency)}
        </div>
      )}

      {/* Skills */}
      {job.skills && job.skills.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-4">
          {job.skills.slice(0, 4).map((skill) => (
            <span
              key={skill}
              className="inline-flex items-center px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-600 rounded"
            >
              {skill}
            </span>
          ))}
          {job.skills.length > 4 && (
            <span className="text-xs text-gray-500">+{job.skills.length - 4} more</span>
          )}
        </div>
      )}

      {/* Footer */}
      <div className="text-xs text-gray-500">{timeAgo(job.published_at)}</div>
    </Link>
  )
}

export function FeaturedJobs({ jobs }: FeaturedJobsProps) {
  if (!jobs || jobs.length === 0) {
    return null
  }

  return (
    <section className="py-16 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="text-3xl font-bold text-gray-900">Featured Jobs</h2>
            <p className="mt-2 text-gray-600">Top opportunities from verified companies</p>
          </div>
          <Link
            to="/jobs"
            className="text-primary-600 hover:text-primary-700 font-medium flex items-center gap-1"
          >
            View All Jobs
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {jobs.slice(0, 6).map((job) => (
            <JobCard key={job.id} job={job} />
          ))}
        </div>
      </div>
    </section>
  )
}
